// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, Form, Row, Select, Switch } from "antd";
import FormField from "../../../components/FormField";
import {
  FIELD_LENGTH,
  LAZY_SELECT_PAGE_SIZE,
  orderDescend,
  TYPE_SURVEY
} from "../../../../utils/Constant";
import { useStore } from "effector-react";
import { BranchesStore } from "../../../../state/branches/BranchesStore";
import { SurveyFormProps } from "../../../types/SurveyTypes";
import DateFormat from "../../../../utils/DateFormat";
import {
  BroadcastListInfoResponseDTO,
  BroadcastListsSearchRequestDTO,
  SurveyInfoResponseDTO
} from "../../../../api/backend-openapi";
import {
  SURVEY_AUDIENCES,
  SurveyAudienceEnum
} from "../../../../utils/enum/survey.audience";
import { LazySelectFormField } from "../../../components/LazySelectFormField";
import { TablePaginationConfig } from "antd/lib/table/interface";
import { requestSearchBroadcastLists } from "../../../../state/broadcastLists/BroadcastListsEffects";
import { preferedRowsPerPageChanged } from "../../../../state/localPreferences/localPreferences";
import { displayError } from "../../../../utils/displayError";

interface Props extends SurveyFormProps {
  survey?: SurveyInfoResponseDTO | null;
  showRequired?: boolean;
  sendPushValueToDisplay: boolean;
  setSendPushValueToDisplay: (value: boolean) => void;
}

const SurveySettingsFormSection: React.FunctionComponent<Props> = (
  props: Props
) => {
  const { t } = useTranslation();

  const {
    survey,
    editMode,
    showRequired,
    sendPushValueToDisplay,
    setSendPushValueToDisplay
  } = props;
  const latestPushDate = survey?.latestPushDate;

  const [isBroadcastListRequired, setIsBroadcastListRequired] = useState(
    survey?.audience === SurveyAudienceEnum.BROADCAST_LIST
  );

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: LAZY_SELECT_PAGE_SIZE,
    showSizeChanger: true
  });
  const [query, setQuery] = useState<string | undefined>(undefined);
  const [paginationOver, setPaginationOver] = useState<boolean>(false);
  const [broadcastListsLoading, setBroadcastListsLoading] = useState<boolean>(
    false
  );
  const [paginatedBroadcastLists, setPaginatedBroadcastLists] = useState<
    BroadcastListInfoResponseDTO[]
  >([]);

  const branchList = useStore(BranchesStore);

  useEffect(() => {
    setBroadcastListsLoading(true);
    const searchCriteria: BroadcastListsSearchRequestDTO = {
      name: query
    };
    requestSearchBroadcastLists({
      pagination,
      sorter: {
        columnKey: "creationDate",
        order: orderDescend
      },
      dto: searchCriteria
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, pagination.current]);

  useEffect(() => {
    return requestSearchBroadcastLists.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setPaginatedBroadcastLists(
          paginatedBroadcastLists?.concat(result.data.listBroadcastListDTO) ??
            result.data.listBroadcastListDTO
        );

        setBroadcastListsLoading(false);
        setPagination({
          ...pagination,
          total: result.data.count
        });
        setPaginationOver(paginatedBroadcastLists.length === result.data.count);
        preferedRowsPerPageChanged(pagination.pageSize);
      } else {
        displayError("news.load_failed");
      }
    });
  });

  return (
    <>
      <Row>
        <Col xs={24}>
          <FormField
            labelName={t("survey.title")}
            inputId="title"
            required={showRequired}
            emptyErrorMessage={t("global.empty_error_field")}
            readOnly={!editMode}
            maxLength={FIELD_LENGTH.NAME}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={24} sm={12}>
          <Form.Item
            name="branch"
            key="branch"
            rules={[
              {
                required: showRequired,
                message: t("global.empty_error_field"),
                whitespace: true
              }
            ]}
            label={<span>{t("add_survey.branch")}</span>}
          >
            <Select
              id="branch"
              placeholder={t("add_news.theme.placeholder")}
              disabled={!editMode}
            >
              {Object.keys(branchList).map(key => (
                <Select.Option key={key} value={key}>
                  {branchList[key].label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="type"
            key="type"
            label={<span>{t("add_survey.type.select")}</span>}
            required={showRequired}
            rules={[
              {
                required: showRequired,
                message: t("global.empty_error_field"),
                whitespace: showRequired
              }
            ]}
            initialValue={survey?.type}
          >
            <Select
              id="type"
              placeholder={t("add_survey.type.placeholder")}
              disabled={!editMode}
            >
              {TYPE_SURVEY.map(typeSurvey => (
                <Select.Option key={typeSurvey.label} value={typeSurvey.label}>
                  {t("add_survey.type." + typeSurvey.label)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col xs={24} sm={12}>
          <Form.Item
            name="audience"
            key="audience"
            rules={
              showRequired
                ? [
                    {
                      required: true,
                      message: t("global.empty_error_field"),
                      whitespace: true
                    }
                  ]
                : []
            }
            label={<span>{t("add_survey.audience.select")}</span>}
          >
            <Select
              id="audience"
              placeholder={t("add_survey.audience.placeholder")}
              disabled={!editMode}
              onChange={values =>
                setIsBroadcastListRequired(
                  values === SurveyAudienceEnum.BROADCAST_LIST
                )
              }
            >
              {SURVEY_AUDIENCES.map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {t(option.label)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="broadcastListId"
            key="broadcastListId"
            rules={
              isBroadcastListRequired
                ? [
                    {
                      required: true,
                      message: t("global.empty_error_field"),
                      whitespace: true
                    }
                  ]
                : []
            }
            label={<span>{t("add_survey.broadcast_list.select")}</span>}
          >
            <LazySelectFormField
              id="broadcastListId"
              placeholder={t("add_survey.broadcast_list.placeholder")}
              disabled={!editMode || !isBroadcastListRequired}
              showSearch
              onSearch={(input: string) => {
                setQuery(input);
                setPaginatedBroadcastLists([]);
                setPagination({ ...pagination, current: 1 });
              }}
              onLoadMore={() => {
                setPagination({
                  ...pagination,
                  current: (pagination?.current ?? 1) + 1
                });
              }}
              isPaginationOver={paginationOver}
              loading={broadcastListsLoading}
              options={
                paginatedBroadcastLists
                  ? paginatedBroadcastLists.map(code => {
                      return { value: code.id, label: code.name };
                    })
                  : []
              }
              allowClear
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        {!!latestPushDate && (
          <Col xs={24} sm={12}>
            <FormField
              labelName={t("news.latest_push_date")}
              defaultValue={DateFormat.standardDate(latestPushDate)}
              inputId="latestPushDate"
              readOnly={true}
            />
          </Col>
        )}
        <Col xs={24} sm={!!latestPushDate ? 12 : 24}>
          <Form.Item
            name="sendPush"
            key="sendPush"
            label={<span>{t("news.sendPush")}</span>}
          >
            <Switch
              checked={sendPushValueToDisplay}
              onChange={(e: boolean) => {
                setSendPushValueToDisplay(e);
              }}
              disabled={!editMode}
            />
          </Form.Item>
        </Col>
      </Row>

      {survey && (
        <Row>
          <Col xs={24} md={12}>
            <FormField labelName="Id" inputId="id" readOnly={true} />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <FormField
              labelName={t("survey.creation_date")}
              inputId="creationDate"
              readOnly={true}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <FormField
              labelName={t("survey.modification_date")}
              inputId="modificationDate"
              readOnly={true}
            />
          </Col>
        </Row>
      )}
    </>
  );
};

export default SurveySettingsFormSection;
